<template>
  <div ref="root" class="column-filter">
    <button
      type="button"
      class="column-filter-btn"
      :disabled="loading"
      :aria-expanded="menuOpen"
      aria-haspopup="listbox"
      @click="toggleMenu"
    >
      <span class="column-filter-label">{{ activeLabel }}</span>
      <span class="column-filter-caret" aria-hidden="true">▾</span>
    </button>

    <ul v-if="menuOpen" class="column-filter-menu" role="listbox">
      <li v-for="option in options" :key="option.value" role="option">
        <button
          type="button"
          class="column-filter-option"
          :class="{ active: option.value === modelValue }"
          @click="select(option.value)"
        >
          {{ option.label }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const root = ref(null)
const menuOpen = ref(false)

const activeLabel = computed(() => {
  const activeOption = props.options.find((option) => option.value === props.modelValue)
  return activeOption?.label ?? props.modelValue
})

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function closeMenu() {
  menuOpen.value = false
}

function select(value) {
  if (value !== props.modelValue) {
    emit('update:modelValue', value)
  }
  closeMenu()
}

function handleDocumentClick(event) {
  if (!root.value?.contains(event.target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<style scoped>
.column-filter {
  position: relative;
  min-width: 0;
  flex: 1;
  max-width: 100%;
}

.column-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  max-width: 100%;
  border: none;
  border-radius: 0.5rem;
  padding: 0;
  background: transparent;
  color: #0f172a;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
}

.column-filter-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.column-filter-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.column-filter-caret {
  font-size: 0.875rem;
  color: #64748b;
}

.column-filter-menu {
  position: absolute;
  top: calc(100% + 0.375rem);
  left: 0;
  right: auto;
  z-index: 10;
  min-width: 0;
  max-width: min(calc(100vw - 2.5rem), 20rem);
  width: max-content;
  margin: 0;
  padding: 0.375rem;
  list-style: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
}

@media (max-width: 767px) {
  .column-filter-menu {
    left: auto;
    right: 0;
    width: min(calc(100vw - 2.5rem), 20rem);
  }
}

.column-filter-option {
  display: block;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  color: #334155;
  cursor: pointer;
  font-size: 0.875rem;
  text-align: left;
  white-space: normal;
}

.column-filter-option:hover {
  background: #f8fafc;
}

.column-filter-option.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}
</style>
